// @ts-nocheck
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText"; // Store Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useBillToState } from "@toolkit/Framework/Store/Data/BillTos/BillTosHooks";
import { useCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import { useOnePageCheckoutConfirmation } from "@toolkit/Framework/Store/Toolkit/Pages/OnePageCheckout/OnePageCheckoutHooks";
import React, { FC } from "react";

/* Store Custom Code - Start */
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@store/Overrides/Components/SmallHeadingAndText";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";

const enum fields {
    showPoNumber = "showPoNumber",
    showDeliveryMethod = "showDeliveryMethod",
}
/* Store Custom Code - End */

const PaymentMethod = getWidgetComponent("OnePageCheckoutConfirmation/OnePageCheckoutConfirmationPaymentMethod");

export interface OnePageCheckoutConfirmationOrderInformationProps extends WidgetProps {
    /* Store Custom Code - Start */
    fields: {
        [fields.showPoNumber]: boolean;
        [fields.showDeliveryMethod]: boolean;
    };
    /* Store Custom Code - End */
}

export interface OnePageCheckoutConfirmationOrderInformationStyles {
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    vatNumberGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderDetailsGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    block?: BlockProps;
    deliveryMethodGridItem?: GridItemProps;
    paymentMethodGridItem?: GridItemProps;
    // Store Custom Code
    requestedDateGridItem?: GridItemProps;
}

export const onePageCheckoutConfirmationOrderInformationStyles: OnePageCheckoutConfirmationOrderInformationStyles = {
    block: orderProps.block,
    orderNumberHeading: orderProps.subtitle,
    headingAndTextStyles: {
        heading: infoProps.label,
        text: infoProps.value,
    },
    orderDetailsGridContainer: infoProps.grid?.container,
    orderDateGridItem: infoProps.grid?.item,
    poNumberGridItem: infoProps.grid?.item,
    statusGridItem: infoProps.grid?.item,
    vatNumberGridItem: infoProps.grid?.item,
    deliveryMethodGridItem: infoProps.grid?.item,
    paymentMethodGridItem: infoProps.grid?.item,
    notesGridItem: infoProps.grid?.item /* Store Custom Code */,
    // Store Custom Code
    requestedDateGridItem: infoProps.grid?.item,
};

const styles = onePageCheckoutConfirmationOrderInformationStyles;

const OnePageCheckoutConfirmationOrderInformation: FC<OnePageCheckoutConfirmationOrderInformationProps> = ({
    fields /* Store Custom Code */,
}) => {
    const { cartId } = useOnePageCheckoutConfirmation();
    const cartState = useCartState(cartId).value;
    const { enableVat } = useSettingsCollection().productSettings;
    const billTo = useBillToState(cartState?.billToId).value;
    const { language } = useSession();

    if (!cartState || !billTo) {
        return null;
    }

    return (
        <Block {...styles.block}>
            <Typography {...styles.orderNumberHeading}>
                {`${translate("Order")} #`}
                <Typography {...styles.orderNumberText}>{cartState.erpOrderNumber || cartState.orderNumber}</Typography>
            </Typography>
            <GridContainer {...styles.orderDetailsGridContainer}>
                <GridItem {...styles.orderDateGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Order Date")}
                        text={
                            cartState?.orderDate
                                ? getLocalizedDateTime({
                                      dateTime: new Date(cartState.orderDate as Date),
                                      language,
                                  })
                                : ""
                        }
                    />
                </GridItem>
                {cartState.poNumber && fields.showPoNumber /* Store Custom Code */ ? (
                    <GridItem {...styles.poNumberGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PO Number")}
                            text={cartState.poNumber}
                        />
                    </GridItem>
                ) : null}
                {cartState.status ? (
                    <GridItem {...styles.statusGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Status")}
                            text={cartState.statusDisplay}
                        />
                    </GridItem>
                ) : null}
                {/* Store Custom Code - Start */}
                {cartState.requestedDeliveryDateDisplay ? (
                    <GridItem {...styles.statusGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Request Delivery Date")}
                            text={
                                <LocalizedDateTime
                                    dateTime={new Date(cartState.requestedDeliveryDateDisplay)}
                                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                />
                            }
                        />
                    </GridItem>
                ) : null}
                {/* Store Custom Code - End */}
                {enableVat && cartState.customerVatNumber ? (
                    <GridItem {...styles.vatNumberGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("VAT Number")}
                            text={cartState.customerVatNumber}
                        />
                    </GridItem>
                ) : null}
                {/* Store Custom Code - Start */}
                {fields.showDeliveryMethod && (
                    <GridItem {...styles.deliveryMethodGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Delivery method")}
                            text={
                                cartState.fulfillmentMethod === FulfillmentMethod.PickUp
                                    ? translate("Store pickup at ") +
                                      (cartState.defaultWarehouse?.description ||
                                          cartState.defaultWarehouse?.name ||
                                          "")
                                    : cartState.carrier && cartState.shipVia
                                      ? `${translate("Ship to home via")} ${cartState.carrier.description} - ${
                                            cartState.shipVia.description
                                        }`
                                      : translate("Ship to home")
                            }
                        />
                    </GridItem>
                )}
                {/* Store Custom Code - End */}
                <GridItem {...styles.paymentMethodGridItem}>
                    <PaymentMethod />
                </GridItem>
                {/* Store Custom Code - Start */}
                {cartState.notes ? (
                    <GridItem {...styles.notesGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Shipping Notes")}
                            text={cartState.notes}
                        />
                    </GridItem>
                ) : null}
                {/* Store Custom Code - End */}
            </GridContainer>
            {/* Store Moved - notesGridItem - up */}
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutConfirmationOrderInformation,
    definition: {
        displayName: "Order Information",
        /* Store Custom Code - Start */
        fieldDefinitions: [
            {
                name: fields.showPoNumber,
                displayName: "Show PO Number",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showDeliveryMethod,
                displayName: "Show Delivery Method",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
        /* Store Custom Code - End */
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
        group: "One Page Checkout Confirmation",
    },
};

export default widgetModule;
